/**
 * Shared hook for consuming a backend grid-spec descriptor.
 *
 * Every grid that draws its columns from the odl_app `/grid-spec/` endpoint
 * goes through this hook so there is exactly one way to fetch the descriptor
 * and one way to read it. The descriptor schema is uniform across every
 * resource (`{ columns, default_sort, page_size, max_page_size,
 * default_visible_columns }`); this hook owns the `.columns` extraction so no
 * callsite repeats it.
 *
 * The spec-query mechanism is injected via DataGridConfigContext so the hook
 * is not hard-coupled to the ODL RTK Query slice. When no provider is mounted
 * (tests, generic contexts) the context's no-op default returns empty columns
 * immediately with no network request.
 *
 * Contains:
 * - useGridSpec — fetch + cache a resource's grid-spec, return its columns
 */

import useDataGridConfig from '@sophocles/data-grids/config/useDataGridConfig';

/**
 * Fetches (and caches) the grid-spec descriptor for `path` and returns its
 * columns array plus the raw descriptor. Specs are stable per resource, so the
 * cache dedupes them across every grid on the same path.
 *
 * @param {string|null|undefined} path  odl_app resource path without a trailing
 *   slash, e.g. `'sites/1/cameras'`. A falsy path skips the request (returns
 *   empty columns) — pass `null` while the path's inputs (siteId, …) resolve.
 * @param {object} [scopeParams]  List-scope params the backend uses to resolve
 *   request-dependent descriptor columns.
 * @returns {{ specColumns: object[], descriptor: object|undefined, isLoading: boolean, isError: boolean }}
 */
export function useGridSpec(path, scopeParams) {
  const config = useDataGridConfig();
  // config.useSpecQuery is injected by DataGridConfigProvider. The ODL config
  // supplies the entitiesApi RTK Query hook; the generic default is a no-op
  // that returns empty/unloaded state without a network call.
  const { data, isLoading, isError } = config.useSpecQuery(path, config.paramSerializer(scopeParams ?? {}));
  // The descriptor schema is uniform; pull the columns array here so callers
  // never reach into `.columns` themselves.
  return { specColumns: data?.columns ?? [], descriptor: data, isLoading, isError };
}
